import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import { config } from "../environment";
import { useSessionBootstrap } from "./useSessionBootstrap";
import { readTrustedSignInCallbackURL } from "../lib/signInCallbackURL";
import { Route, routes, Switch } from "../lib/urls";
import { SessionTenantOriginProvider } from "../hooks/useSessionTenantOrigin";
import { AppLoading } from "./AppLoading";
import { LicenseGate } from "./LicenseGate";
import { SignedIn } from "./SignedIn";
import { SignIn } from "./SignIn";
import { AuthError } from "./SignIn/AuthError";
import { ToastProvider } from "./Toast";
import { Version } from "./Version";
import { ZeroSetup } from "./ZeroSetup";

export function App() {
  return <AppRoutes />;
}

function AppRoutes() {
  const { hasLoaded, user } = useSessionBootstrap();

  useEffect(() => {
    if (user) {
      Sentry.setUser({ id: user.id, email: user.email });
    } else {
      Sentry.setUser(null);
    }
  }, [user]);

  // Trusted callbacks from the edge server’s SSO flow must complete even when the user is already signed in.
  const pendingCallbackURL =
    hasLoaded && user
      ? readTrustedSignInCallbackURL({
          tenantOrigin: user.tenantOrigin,
          tenantEdgeOrigin: user.tenantEdgeOrigin,
          tenantEdgeWildcardDomain: user.tenantEdgeWildcardDomain,
          tenantCustomDomains: user.tenantCustomDomains,
          tenantApiOrigin: user.tenantApiOrigin,
        })
      : null;

  useEffect(() => {
    if (pendingCallbackURL) {
      window.location.href = pendingCallbackURL;
    }
  }, [pendingCallbackURL]);

  if (!hasLoaded) return <AppLoading />;
  if (pendingCallbackURL) return <AppLoading />;

  return (
    <>
      <title>{config.title}</title>
      <Switch>
        <Route path={routes.version}>
          <Version />
        </Route>
        <Route path={routes.authError}>
          <ToastProvider>
            <AuthError />
          </ToastProvider>
        </Route>
        <Route>
          {user ? (
            <SessionTenantOriginProvider
              origin={user.tenantOrigin}
              edgeOrigin={user.tenantEdgeOrigin}
              edgeWildcardDomain={user.tenantEdgeWildcardDomain}
              apiOrigin={user.tenantApiOrigin}
            >
              <LicenseGate>
                <ZeroSetup user={user}>
                  <SignedIn />
                </ZeroSetup>
              </LicenseGate>
            </SessionTenantOriginProvider>
          ) : (
            <ToastProvider>
              <SignIn />
            </ToastProvider>
          )}
        </Route>
      </Switch>
    </>
  );
}
